import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { AlertTriangle, Check, Link, TrendingUp, X } from "react-feather";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { formatMoney, formatAmount } from "../../../utils/formatMoney";
import { formatShortDate, PERMISSIONS } from "./glUtils";
import { ConfidenceBadge, incomeTypeLabel, RECON_STATUS, ReconStatusBadge } from "./incomeReconMeta";

const DECISION_TOAST = {
  confirm: "Match confirmed",
  reject: "Match rejected",
  unmatch: "Marked as unmatched",
};

/** Shows one GL entry line with currency and amount */
function EntryLine({ entry, role, incomeCurrency, fxRate, convertedAmount }) {
  const entryCurrency = entry.currency_code;
  const hasCurrencyMismatch = incomeCurrency && entryCurrency && incomeCurrency !== entryCurrency;

  return (
    <div className="gl-entry-line">
      <div className="gl-entry-main">
        <div className="gl-cell-strong">{entry.counterparty || entry.label || `GL transaction #${entry.id}`}</div>
        <div className="gl-desc-meta gl-muted">
          {formatShortDate(entry.booking_date)} · {entry.label || "—"}
          {entry.reference ? ` · ref ${entry.reference}` : ""}
        </div>
      </div>
      <div className="gl-entry-amount">
        <span className={hasCurrencyMismatch ? "text-warning" : ""}>
          {formatMoney(entry.credit, entryCurrency)}
        </span>
        {hasCurrencyMismatch && fxRate && (
          <small className="gl-muted d-block" title={`Exchange rate: 1 ${entryCurrency} = ${fxRate} ${incomeCurrency}`}>
            <TrendingUp size={9} className="me-1" />
            ≈ {formatAmount(convertedAmount ?? (entry.credit * fxRate), incomeCurrency)} {incomeCurrency}
            <span className="ms-1 text-muted" style={{ fontSize: "0.7em" }}>@ {fxRate}</span>
          </small>
        )}
        {role && <small className="gl-muted d-block">{role}</small>}
      </div>
    </div>
  );
}

function CandidateCard({ candidate, index, selected, onSelect, disabled, incomeCurrency }) {
  const fxRate = candidate.fx_rate ?? null;
  const convertedAmount = candidate.converted_amount ?? null;
  const entryCurrency = candidate.entries?.[0]?.currency_code ?? null;
  const hasCurrencyMismatch = incomeCurrency && entryCurrency && incomeCurrency !== entryCurrency;

  return (
    <label className={`gl-candidate-card ${selected ? "selected" : ""} ${disabled ? "disabled" : ""}`}>
      <div className="gl-candidate-head">
        <Input
          type="radio"
          name="gl-income-candidate"
          aria-label={`Use GL transaction candidate ${index + 1}`}
          checked={selected}
          disabled={disabled}
          onChange={() => onSelect(index)}
        />
        <ConfidenceBadge score={candidate.confidence} />
        <ReconStatusBadge status={candidate.status} matchType={candidate.match_type} />
        {hasCurrencyMismatch && (
          <span className="gl-flag gl-flag-info" title={`Currency conversion: ${entryCurrency} → ${incomeCurrency}`}>
            <TrendingUp size={11} /> FX {entryCurrency}→{incomeCurrency}
          </span>
        )}
        {candidate.ambiguous && (
          <span className="gl-flag gl-flag-warn" title="Another transaction scores almost the same">
            <AlertTriangle size={11} /> ambiguous
          </span>
        )}
      </div>

      {/* Currency conversion summary if currencies differ */}
      {hasCurrencyMismatch && (
        <div className="gl-fx-summary">
          {fxRate ? (
            <span>
              <TrendingUp size={11} className="me-1" />
              Rate: <strong>1 {entryCurrency} = {fxRate} {incomeCurrency}</strong>
              {convertedAmount != null && (
                <> · GL total ≈ <strong>{formatMoney(convertedAmount, incomeCurrency)}</strong></>
              )}
            </span>
          ) : (
            <span className="text-warning">
              <AlertTriangle size={11} className="me-1" />
              No exchange rate found for {entryCurrency}/{incomeCurrency} — amounts may not be comparable
            </span>
          )}
        </div>
      )}

      <div className="gl-candidate-entries">
        {(candidate.entries || []).map((entry) => (
          <EntryLine
            key={entry.id}
            entry={entry}
            incomeCurrency={incomeCurrency}
            fxRate={fxRate}
            convertedAmount={convertedAmount}
          />
        ))}
      </div>
      <div className="gl-candidate-reasons">
        {(candidate.reasons || []).map((reason) => (
          <span key={reason} className="gl-flag gl-flag-ok">
            {reason}
          </span>
        ))}
        {(candidate.discrepancies || []).map((item) => (
          <span key={item} className="gl-flag gl-flag-warn">
            {item}
          </span>
        ))}
      </div>
    </label>
  );
}

export default function IncomeReconReviewModal({ item, meta, onClose }) {
  const queryClient = useQueryClient();
  const income = item.income;
  const [selected, setSelected] = useState(0);
  const [notes, setNotes] = useState("");
  const [combined, setCombined] = useState(false);
  const [matchGroup, setMatchGroup] = useState("");
  const [linkParty, setLinkParty] = useState(false);

  const canMutate = Boolean(meta?.permissions?.[PERMISSIONS.ANNOTATE]);

  const detailQuery = useQuery({
    queryKey: ["glIncomeReconDetail", income.income_type, income.income_id],
    queryFn: () => GeneralLedgerService.getIncomeReconciliationDetail(income.income_type, income.income_id),
  });

  // Fetch detail and extract useful data
  const detail = detailQuery.data?.data;
  const candidates = detail?.candidates || [];
  const scope = detail?.scope || null;

  // Artist identity: prefer scope data (from backend artist resolution) over raw income fields
  const scopeArtist = scope?.artist;
  const artistDisplayName = scopeArtist?.name || income.artist_name || income.title;
  const artistFullLegalName = scopeArtist?.full_name || income.artist_full_name;

  // Bank names / aliases from the Parties tab (already resolved server-side via CounterpartyGroups)
  const bankNames = (scope?.names || []).filter(
    (n) => n && n !== artistDisplayName && n !== artistFullLegalName
  );

  const decision = detail?.decision ?? item.decision ?? null;
  const confirmed = decision?.status === "confirmed";
  const links = item.links || [];
  const selectedCandidate = candidates[selected];
  const selectedEntryIds = (selectedCandidate?.entries || []).map((entry) => entry.id);
  const canConfirm = canMutate && !confirmed && selectedEntryIds.length > 0 && (!combined || matchGroup.trim());

  const mutation = useMutation({
    mutationFn: ({ action, payload }) => {
      if (action === "confirm") return GeneralLedgerService.confirmIncomeReconciliation(payload);
      if (action === "reject") return GeneralLedgerService.rejectIncomeReconciliation(payload);
      return GeneralLedgerService.unmatchIncomeReconciliation(payload);
    },
    onSuccess: (_, { action }) => {
      toast.success(DECISION_TOAST[action]);
      queryClient.invalidateQueries({ queryKey: ["glIncomeRecon"] });
      onClose();
    },
    onError: (error) => toast.error(getApiErrorMessage(error, "Could not save the decision.")),
  });

  const busy = mutation.isPending;

  const confirmMatch = () =>
    mutation.mutate({
      action: "confirm",
      payload: {
        income_type: income.income_type,
        income_id: income.income_id,
        entry_ids: selectedEntryIds,
        notes: notes.trim() || null,
        ...(combined && matchGroup.trim() ? { match_group: matchGroup.trim() } : {}),
        ...(linkParty ? { link_counterparty: true } : {}),
      },
    });

  const rejectMatch = () =>
    mutation.mutate({
      action: "reject",
      payload: {
        income_type: income.income_type,
        income_id: income.income_id,
        entry_ids: selectedEntryIds,
        reason: notes.trim() || null,
      },
    });

  const markUnmatched = () =>
    mutation.mutate({
      action: "unmatch",
      payload: {
        income_type: income.income_type,
        income_id: income.income_id,
        reason: notes.trim() || null,
      },
    });

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Reconcile "{income.title || `income #${income.income_id}`}"</ModalHeader>
      <ModalBody>
        {/* ── Income summary panel ── */}
        <div className="gl-panel gl-recon-income-panel mb-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <div className="gl-cell-strong">{income.title || `Income #${income.income_id}`}</div>

              {/* Artist identity block */}
              {(artistDisplayName || artistFullLegalName) && (
                <div className="gl-artist-identity mt-1">
                  {artistDisplayName && (
                    <span className="gl-artist-stage">
                      <strong>{artistDisplayName}</strong>
                    </span>
                  )}
                  {artistFullLegalName && artistFullLegalName !== artistDisplayName && (
                    <span className="gl-artist-legal ms-2">
                      <Link size={11} className="me-1 text-muted" />
                      <span className="text-muted small">Legal name: </span>
                      <strong className="small">{artistFullLegalName}</strong>
                    </span>
                  )}
                </div>
              )}

              {/* Bank aliases resolved from the Parties tab */}
              {bankNames.length > 0 && (
                <div className="gl-bank-aliases mt-1">
                  <span className="gl-muted small">Bank names: </span>
                  {bankNames.map((name) => (
                    <span key={name} className="gl-alias-badge ms-1">{name}</span>
                  ))}
                </div>
              )}

              <div className="gl-muted small mt-1">
                {incomeTypeLabel(income.income_type, income.provider)} · {income.provider || "unknown provider"} ·{" "}
                {income.period_start || "—"} → {income.period_end || "—"}
              </div>
            </div>
            <div className="text-end">
              <div className="gl-amount">{formatMoney(income.amount, income.currency)}</div>
              <div className="gl-muted small">
                {income.currency && <span className="gl-currency-badge me-1">{income.currency}</span>}
                income recorded
              </div>
            </div>
          </div>
        </div>

        {income.breakdown?.length > 0 && (
          <div className="gl-card mb-3 p-2 border rounded" style={{ background: "#f8f9fa" }}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="fw-bold text-uppercase text-muted">
                Pooled Artists ({income.artist_count || income.breakdown.length})
              </small>
              <small className="text-muted">Total: {formatMoney(income.amount, income.currency)}</small>
            </div>
            <div style={{ maxHeight: "150px", overflowY: "auto", fontSize: "0.85rem" }}>
              {income.breakdown.map((item, idx) => (
                <div key={item.artist_id || idx} className="d-flex justify-content-between py-1 border-bottom border-light">
                  <span>
                    <strong>{item.artist_name || "Unknown"}</strong>
                    {item.artist_full_name && item.artist_full_name !== item.artist_name && (
                      <span className="text-muted ms-1">({item.artist_full_name})</span>
                    )}
                  </span>
                  <span className="gl-mono">{formatMoney(item.amount, income.currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {confirmed && (
          <div className="alert alert-success py-2 small">
            <Check size={13} className="me-1" />
            Confirmed{decision?.decided_at ? ` on ${formatShortDate(decision.decided_at.slice(0, 10))}` : ""}. The
            reconciliation is already recorded and is never overwritten silently.
          </div>
        )}

        {confirmed && links.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-2">Linked General Ledger transactions</h6>
            {links.map((link) => (
              <EntryLine
                key={link.gl_entry_id}
                entry={link.entry || { id: link.gl_entry_id }}
                role={link.role}
                incomeCurrency={income.currency}
              />
            ))}
          </div>
        )}

        {!confirmed && decision && (
          <div className="alert alert-warning py-2 small">
            Currently marked as <strong>{RECON_STATUS[decision.status]?.label || decision.status}</strong>. Choose a
            transaction below to confirm, or keep it unmatched. Every decision is kept in the audit trail.
          </div>
        )}

        {!confirmed && (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">Candidate General Ledger transactions</h6>
              {detailQuery.isFetching && <Spinner size="sm" />}
            </div>

            {scope && (
              <div className={`alert ${scope.mode === "artist_fallback" ? "alert-warning" : "alert-info"} py-2 small`}>
                {scope.mode === "artist_fallback" ? (
                  <>
                    No General Ledger transaction is linked to <strong>{artistDisplayName}</strong> yet. All credit
                    transactions are shown. Confirm the right one and tick "link this counterparty to the artist" below
                    to teach the system for future statements.
                  </>
                ) : (
                  <>
                    Showing only transactions related to <strong>{artistDisplayName}</strong>
                    {artistFullLegalName && artistFullLegalName !== artistDisplayName && (
                      <> (legal name: <strong>{artistFullLegalName}</strong>)</>
                    )}
                    {bankNames.length > 0 && (
                      <> · bank names: {bankNames.map((n, i) => <strong key={n}>{i > 0 ? ", " : ""}{n}</strong>)}</>
                    )}
                    .
                  </>
                )}
              </div>
            )}

            {detailQuery.isLoading ? (
              <div className="gl-muted py-3 text-center">Looking for matching transactions…</div>
            ) : (
              <div className="gl-candidate-list">
                {candidates.length === 0 && (
                  <div className="alert alert-info py-2 mb-0">
                    No GL transaction matches this income for the selected artist. Mark it as unmatched, or reject it to keep it out of the
                    candidate list.
                  </div>
                )}
                {candidates.map((candidate, index) => (
                  <CandidateCard
                    key={candidate.entry?.id || candidate.entries?.map((e) => e.id).join("-") || index}
                    candidate={candidate}
                    index={index}
                    selected={selected === index}
                    onSelect={setSelected}
                    disabled={busy}
                    incomeCurrency={income.currency}
                  />
                ))}
              </div>
            )}

            <div className="mt-3">
              <Label>Note / reason</Label>
              <Input
                type="textarea"
                rows={2}
                maxLength={500}
                placeholder="Why this match is right, or why it was rejected…"
                value={notes}
                disabled={busy}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {canMutate && (
              <div className="form-check mt-2">
                <Input
                  type="checkbox"
                  id="gl-income-combined"
                  checked={combined}
                  disabled={busy}
                  onChange={(e) => setCombined(e.target.checked)}
                />
                <Label for="gl-income-combined" className="form-check-label small">
                  Part of a combined match (several incomes share these GL transactions)
                </Label>
              </div>
            )}
            {combined && (
              <div className="mt-2">
                <Label>Combined match group</Label>
                <Input
                  value={matchGroup}
                  maxLength={64}
                  placeholder="Same value for each income in this group"
                  disabled={busy}
                  onChange={(e) => setMatchGroup(e.target.value)}
                />
              </div>
            )}

            {(artistDisplayName || artistFullLegalName) && canMutate && (
              <div className="form-check mt-3">
                <Input
                  type="checkbox"
                  id="gl-income-link-party"
                  checked={linkParty}
                  disabled={busy}
                  onChange={(e) => setLinkParty(e.target.checked)}
                />
                <Label for="gl-income-link-party" className="form-check-label small">
                  Link this bank counterparty to artist{" "}
                  <strong>{artistFullLegalName || artistDisplayName}</strong>
                  {artistFullLegalName && artistFullLegalName !== artistDisplayName && (
                    <> (<em>{artistDisplayName}</em>)</>
                  )}{" "}
                  so future statements are recognized automatically.
                </Label>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-between">
        {canMutate && !confirmed ? (
          <Button color="link" className="text-danger px-0" disabled={busy} onClick={markUnmatched}>
            No matching transaction
          </Button>
        ) : (
          <span className="gl-muted small">
            {canMutate ? "This reconciliation is confirmed." : "You can view this reconciliation but not change it."}
          </span>
        )}
        <div className="d-flex gap-2">
          <Button color="secondary" onClick={onClose}>
            Close
          </Button>
          {canMutate && !confirmed && (
            <>
              <Button color="light" disabled={busy || selectedEntryIds.length === 0} onClick={rejectMatch}>
                <X size={13} /> Reject match
              </Button>
              <Button color="primary" disabled={busy || !canConfirm} onClick={confirmMatch}>
                <Check size={13} /> {busy ? "Saving…" : "Confirm match"}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
}
